'use client';

import React, { useCallback, useContext, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import MainContainer from 'layout/src/components/MainContainer';
import DropdownContext from 'layout/src/components/SecondaryNavigation/_dropdownContext';
import IconComponent from 'ui/src/components/Icon';
import twMerge from 'utilities/src/twMerge';
import { getCategoryPath } from 'data/taxonomy';
import CategoriesMenu from './_CategoriesMenu';
import SearchField from './_SearchField';
import useDismiss from './_useDismiss';
import { headerButtonId } from '../Layout/Header';
import type { NavbarData } from './types';

const MENU_ID = 'blog-categories-menu';
const TRIGGER_ID = 'blog-categories-trigger';
// The triggers toggle the menu themselves, so clicking them mustn't also count as a click outside.
const MENU_TRIGGER_IDS = [TRIGGER_ID, headerButtonId];
const CATEGORY_PATH = /^\/category\/([^/]+)/;

type Props = NavbarData & {
  activeCategorySlug?: string | null;
  className?: string;
};

export default function Navbar({
  categories,
  priorityCategories,
  popularSearches,
  recentlyUpdated,
  searchPlaceholder,
  activeCategorySlug,
  className,
}: Props) {
  const pathname = usePathname();
  const activeSlug = activeCategorySlug ?? pathname?.match(CATEGORY_PATH)?.[1];
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // Below 640px there's no bar: the header's Blog button opens the menu instead.
  const { isOpen: isMobileMenuOpen, setIsOpen: setIsMobileMenuOpen } = useContext(DropdownContext);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMenus = useCallback(() => {
    setIsMenuOpen(false);
    setIsMobileMenuOpen(false);
  }, [setIsMobileMenuOpen]);

  const isCategoriesMenuOpen = isMenuOpen || isMobileMenuOpen;
  useDismiss(menuRef, isCategoriesMenuOpen, closeMenus, MENU_TRIGGER_IDS);

  const searchProps = { popularSearches, recentlyUpdated, searchPlaceholder };

  return (
    // -mt-10 cancels the header's bottom margin so the bar (or, below 640px, the page) sits right under it
    <nav aria-label="Blog" className={twMerge('relative z-30 -mt-10', className)}>
      <div className="bg-site-neutrals-25 mdS:block hidden">
        <MainContainer className="flex h-16 items-center gap-8">
          <div className="flex h-full min-w-0 flex-1 items-center gap-10">
            <button
              id={TRIGGER_ID}
              type="button"
              aria-expanded={isMenuOpen}
              aria-controls={MENU_ID}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="site-paragraph-m text-site-neutrals-900 flex shrink-0 items-center gap-2 font-bold"
            >
              All Categories
              <IconComponent
                iconName="ExpandMore"
                size="IconS"
                className={twMerge('bg-current transition-transform duration-200', { 'rotate-180': isMenuOpen })}
              />
            </button>
            <div className="bg-site-neutrals-200 lgS:block hidden h-6 w-px shrink-0" />
            <ul className="lgS:flex hidden h-full gap-6">
              {priorityCategories.map(({ slug, label }) => (
                <li key={slug}>
                  <Link
                    href={getCategoryPath(slug)}
                    prefetch={false}
                    aria-current={slug === activeSlug ? 'true' : undefined}
                    className={twMerge(
                      'site-paragraph-m flex h-full items-center whitespace-nowrap border-b-2 px-2.5 font-medium',
                      slug === activeSlug
                        ? 'border-site-primary-600 text-site-neutrals-900'
                        : 'text-site-neutrals-600 hover:border-site-neutrals-100 hover:text-site-neutrals-900 border-transparent',
                    )}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <SearchField
            {...searchProps}
            dimPage
            className="w-[18.75rem] shrink-0 self-stretch"
            dropdownClassName="right-0 mt-1 w-[35rem]"
          />
        </MainContainer>
      </div>
      <div
        aria-hidden="true"
        className={twMerge(
          'bg-site-neutrals-900/30 fixed inset-0 -z-10 transition-all duration-200',
          isCategoriesMenuOpen ? 'visible opacity-100' : 'invisible opacity-0',
        )}
      />
      <CategoriesMenu
        ref={menuRef}
        id={MENU_ID}
        isOpen={isCategoriesMenuOpen}
        categories={categories}
        activeCategorySlug={activeSlug}
        onNavigate={closeMenus}
        search={
          <SearchField
            {...searchProps}
            className="mdS:hidden"
            onNavigate={closeMenus}
            dropdownClassName="-inset-x-4 z-10 mt-4 rounded-none border-0 px-4"
          />
        }
      />
    </nav>
  );
}
